'use client'

import { SALES_FORM_URL } from '@/lib/salesForm'
import { useState } from 'react'
import { ArrowRight, Check, CircleHelp } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useBillingPricingInfo } from '@/hooks/useBillingPricingInfo'
import { trackMarketingDemoInteracted } from '@/lib/analytics'
import { isByokAnnualEnabled, isByokPlansEnabled } from '@/lib/byokPlansFlag'
import { BYOK_ENTERPRISE, BYOK_PLANS, type ByokPlan } from './byokPlanData'
import { byokPlanPrice, byokPriceNote } from './launchOffer'
import { ByokPeriodSwitch } from './ByokPeriodSwitch'
import { useLaunchOffer } from './useLaunchOffer'

// Free, Pro and Studio go straight into fal setup for the plan (signed-out visitors get the
// signup modal over it) once NEXT_PUBLIC_BYOK_PLANS_ENABLED is on; Enterprise goes to the contact form. The flow's
// trial step starts the plan for the period picked here.

// Hover text for feature labels that need a line of explanation (same idea as the olive cards).
const FEATURE_TIPS: Record<string, string> = {
  'Hands-on production support':
    'Pipeline, production and creative help from our creative technologists, shaped around your team.',
}

/**
 * fal setup for a plan card, with the billing period when it is annual and the plan has a trial to pay for
 * (Free has no period), and `via=pricing` for the events. Same URL as falOnboardingPath, which this marketing page
 * doesn't import.
 */
function falSetupHref(plan: ByokPlan, yearly: boolean): string {
  return `/welcome?intent=fal&plan=${plan.name.toLowerCase()}${yearly && plan.trial ? '&period=annual' : ''}&via=pricing`
}

export const MEDIA_SECONDS_NOTE =
  'Minutes of video, audio and images Martini processes for you each month, generated or uploaded. An image counts as 10 seconds.'

// Same card anatomy as PlanCards: name, price, who it's for, hairline, the one
// numbers that differ (minutes, olives), features, CTA pinned to the bottom.
export function ByokPlans() {
  const [yearly, setYearly] = useState(false)
  // Signed in with the free month used (here or on a team): the trial plans subscribe at their price. Signed-out
  // visitors, and a person whose trial state can't be read, keep the trial copy.
  const trialUsed = useBillingPricingInfo().billingPricingInfo?.trialAvailable === false
  // The launch price, monthly or annual (annual $15 / $95, up to 40% less). No end date on the
  // page. ?offer=off shows the regular prices (launchOffer.ts).
  const offer = useLaunchOffer()
  const cards = [...BYOK_PLANS, BYOK_ENTERPRISE].map(plan => ({ plan, ...byokPlanPrice(plan, { offer, yearly }) }))
  // Until the plans are purchasable the cards show the plan and no way into fal setup (Enterprise keeps its form).
  const plansOpen = isByokPlansEnabled()
  // The Monthly / Annual switch; the period rides along into fal setup and checkout (`period=annual`).
  const annualOpen = isByokAnnualEnabled()
  return (
    <div>
      {/* Billing period: quieter than the plan-type switch above it, and only on this side. The
          launch pricing sits on the same row, so it reads as part of the price, not a banner. */}
      {offer || annualOpen ? (
        <div
          className={`mb-5 flex flex-col items-center gap-3 text-sm sm:flex-row ${offer ? 'sm:justify-between' : 'sm:justify-end'}`}
        >
          {offer && <p className="text-white/55">Launch pricing.</p>}
          {annualOpen ? (
            <ByokPeriodSwitch
              yearly={yearly}
              offer={offer}
              onChange={y => {
                setYearly(y)
                trackMarketingDemoInteracted({ demo: 'byok_billing_period', action: y ? 'annual' : 'monthly' })
              }}
            />
          ) : null}
        </div>
      ) : null}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map(({ plan, price, regular }) => (
          <div
            key={plan.name}
            className={`relative flex flex-col rounded-2xl border p-6 md:p-7 ${
              plan.highlighted
                ? 'border-white/30 bg-white/[0.06] shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_24px_60px_-20px_rgba(255,255,255,0.12)]'
                : 'border-white/10 bg-white/[0.02]'
            }`}
          >
            <h3 className="text-[15px] font-medium text-white/70">{plan.name}</h3>
            <p className="mt-3 text-[40px] font-semibold leading-none tracking-[-0.03em] text-white">
              {/* The launch price IS the price, the regular one struck beside it (as on the olive cards). */}
              {regular && <span className="me-2 text-xl font-medium text-white/30 line-through">{regular}</span>}
              {price}
              {plan.price.startsWith('$') && (
                <span className="ms-1 text-base font-normal tracking-normal text-white/40">/mo</span>
              )}
            </p>
            {/* Always at least a line, so the cards do not jump when the period changes; a minimum, so a
                note that wraps on a narrow card pushes the tagline down instead of running over it. */}
            <p className="mt-1.5 min-h-4 text-xs text-white/40">{byokPriceNote(plan, { offer, yearly })}</p>
            <p className="mt-2 min-h-[2.75em] text-sm leading-snug text-white/50">{plan.tagline}</p>

            <div className="mt-6 border-t border-white/[0.08] pb-8 pt-6">
              <div>
                <p className="flex items-center gap-1.5 text-sm text-white/65">
                  <span>
                    <span className="font-semibold tabular-nums text-white">{plan.processingMinutes}</span> processing
                    minutes
                  </span>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="What are processing minutes?"
                          className="text-white/35 hover:text-white/80"
                        >
                          <CircleHelp className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px] text-xs">
                        {MEDIA_SECONDS_NOTE}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </p>
                <p className="mt-1 text-xs text-white/40">every month</p>
              </div>
              <div className="mt-4">
                {/* Olives second, as a perk: a few a month on the paid plans, for models not on fal; top-ups on all. */}
                {plan.olives ? (
                  <p className="text-sm text-white/65">
                    <span className="font-semibold tabular-nums text-white">{plan.olives}</span> olives a month
                  </p>
                ) : (
                  <p className="text-sm text-white/65">Olive top-ups any time</p>
                )}
                <p className="mt-1 text-xs text-white/40">For models not on fal</p>
              </div>
              <p className="mt-5 h-4 text-xs text-white/40">{plan.includes ?? ''}</p>
              <ul className="mt-4 space-y-3">
                {plan.features.map(feature => (
                  <li key={feature} className="flex items-start gap-2.5 text-sm leading-snug text-white/65">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-white/35" strokeWidth={2} />
                    {FEATURE_TIPS[feature] ? (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="cursor-help text-start underline decoration-white/20 decoration-dotted underline-offset-[5px] transition-colors hover:text-white"
                            >
                              {feature}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[240px] text-xs">
                            {FEATURE_TIPS[feature]}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span>{feature}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {plan !== BYOK_ENTERPRISE && !plansOpen ? (
              // Same height as the button, so the cards keep lining up.
              <p className="mt-auto flex h-11 items-center justify-center text-sm text-white/45">Available soon</p>
            ) : (
              <a
                {...(plan === BYOK_ENTERPRISE
                  ? { href: SALES_FORM_URL, target: '_blank', rel: 'noopener noreferrer' }
                  : { href: falSetupHref(plan, yearly) })}
                data-cta={plan === BYOK_ENTERPRISE ? 'byok_plan' : 'byok_plan_setup'}
                data-cta-target={plan.name.toLowerCase()}
                className={`group/btn mt-auto inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 text-sm font-semibold transition-colors duration-150 ${
                  plan.highlighted
                    ? 'bg-white text-black hover:bg-white/90'
                    : 'border border-white/15 text-white hover:bg-white/[0.08]'
                }`}
              >
                {plan.trial && trialUsed ? 'Subscribe' : plan.cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/btn:translate-x-0.5" />
              </a>
            )}
            {/* Always rendered, so the buttons line up across cards. Silent until the plans open. */}
            <p className="mt-2.5 h-4 text-center text-xs text-white/40">
              {plan.trial && plansOpen ? (trialUsed ? `Trial used · ${price}/mo` : '30 days free · Card required') : ''}
            </p>
          </div>
        ))}
      </div>

      <p className="mx-auto mt-8 max-w-[720px] text-center text-sm leading-relaxed text-white/45">
        Generations bill to your fal account. Olives cover models not on fal. Agents connect over MCP, CLI and API on
        every fal-key plan, Free included.
      </p>
    </div>
  )
}
